import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  activePrograms,
  buildMyAffiliateLink,
  describeMyAffiliateEmptyState,
  fetchMyAffiliatePrograms,
} from '@/lib/my-affiliate'
import { MyAffiliateEmptyState } from '@/pages/SouAfiliadoPage/MyAffiliateEmptyState'
import type { MyAffiliateProgram } from '@/types/my-affiliate'
import styles from './SouAfiliadoPage.module.css'

export function SouAfiliadoLinksPage() {
  const [programs, setPrograms] = useState<MyAffiliateProgram[]>([])
  const [allPrograms, setAllPrograms] = useState<MyAffiliateProgram[]>([])
  const [emptyMessage, setEmptyMessage] = useState(
    describeMyAffiliateEmptyState(undefined, null, [])
  )
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyAffiliatePrograms()
      setAllPrograms(res.programs)
      setEmptyMessage(
        describeMyAffiliateEmptyState(res.login_context, res.profile, res.programs)
      )
      setPrograms(activePrograms(res.programs))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar links.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const copyLink = (key: string, url: string) => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedKey(key)
      window.setTimeout(() => setCopiedKey(null), 2000)
    })
  }

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  if (programs.length === 0) {
    const inactiveOnly = allPrograms.filter((p) => !p.is_active)

    return (
      <div className={styles.page}>
        <MyAffiliateEmptyState icon="mdi:link-off" message={emptyMessage} />
        {inactiveOnly.length > 0 ? (
          <ul className={styles.linkList}>
            {inactiveOnly.map((program) => (
              <li key={program.affiliate_id} className={styles.programBlock}>
                <div className={styles.programBlockHead}>
                  <h3 className={styles.programBlockTitle}>{program.institution.name}</h3>
                  <span className={styles.badgeInactive}>Inativo</span>
                </div>
                <p className={styles.programMeta}>
                  Código {program.tracking_code} — links indisponíveis até reativação.
                </p>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <p className={styles.filterLabel}>
        Compartilhe os links abaixo. O parâmetro <code>af</code> na URL identifica suas
        indicações no checkout.
      </p>

      <ul className={styles.linkList}>
        {programs.map((program) => (
          <li key={program.affiliate_id} className={styles.programBlock}>
            <div className={styles.programBlockHead}>
              <div>
                <h3 className={styles.programBlockTitle}>{program.institution.name}</h3>
                <span className={styles.trackingCode}>Código: {program.tracking_code}</span>
              </div>
              <span className={styles.badgeActive}>Ativo</span>
            </div>

            {program.allowed_campaigns.length === 0 ? (
              <p className={styles.programMeta}>
                Nenhuma campanha ativa disponível para gerar link neste programa.
              </p>
            ) : (
              <div className={styles.campaignRows}>
                {program.allowed_campaigns.map((campaign) => {
                  const url = buildMyAffiliateLink(campaign, program.tracking_code)
                  const key = `${program.affiliate_id}-${campaign.id}`
                  const label = campaign.title?.trim() || campaign.name
                  const percentLabel =
                    campaign.commission_percent !== undefined
                      ? ` · ${campaign.commission_percent}%`
                      : program.all_campaigns
                        ? ` · ${program.commission_percent}%`
                        : ''

                  return (
                    <div key={key} className={styles.campaignRow}>
                      <span className={styles.campaignName}>
                        {label}
                        {percentLabel}
                      </span>
                      <input
                        type="text"
                        className={styles.linkInput}
                        value={url}
                        readOnly
                        aria-label={`Link da campanha ${label}`}
                      />
                      <button
                        type="button"
                        className={`${styles.copyBtn} ${copiedKey === key ? styles.copied : ''}`}
                        onClick={() => copyLink(key, url)}
                      >
                        <Icon icon={copiedKey === key ? 'mdi:check' : 'mdi:content-copy'} />
                        {copiedKey === key ? 'Copiado' : 'Copiar'}
                      </button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.openLinkBtn}
                      >
                        <Icon icon="mdi:open-in-new" />
                        Abrir
                      </a>
                    </div>
                  )
                })}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
